import { useQuery } from '@tanstack/react-query';

import { AdminNav } from './AdminNav';
import { Alert } from '../../components/ui/Alert';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';

interface Entry {
  id: number;
  action: string;
  subject_type: string;
  subject_id: number | null;
  actor_username: string | null;
  before_state: Record<string, unknown> | null;
  after_state: Record<string, unknown> | null;
  created_at: string;
}

/**
 * The audit log (HANDOFF §6.7).
 *
 * Read-only, and permanently so. An audit trail somebody can edit answers no
 * question worth asking, which is why there is no delete endpoint for it to
 * call.
 */
export function AdminAuditPage() {
  const { data, isPending, error } = useQuery({
    queryKey: ['admin', 'audit'],
    queryFn: () => api.get<{ items: Entry[]; next_cursor: string | null }>('/admin/audit', {
      query: { limit: 100 },
    }),
  });

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5 px-6 py-10">
      <AdminNav />

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Audit log</h1>
        <p className="mt-1 text-sm text-soft">Every privileged write, with what changed.</p>
      </div>

      {isPending && <p className="text-soft">Loading…</p>}
      {error && <Alert tone="error">Could not load the audit log.</Alert>}

      {data && (
        <Card className="divide-y divide-line">
          {data.items.length === 0 && <p className="p-6 text-sm text-soft">Nothing recorded yet.</p>}

          {data.items.map((entry) => (
            <div key={entry.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-4 text-sm">
              <span className="font-mono text-accent">{entry.action}</span>
              <span className="text-soft">
                {/*
                  A null actor is a command-line action - somebody with shell
                  access and no account to attribute it to. That is how the
                  first administrator is created, and it should read as
                  distinct from a person doing it through the panel rather than
                  as missing data.
                */}
                {entry.actor_username ? `by @${entry.actor_username}` : 'from the command line'}
              </span>
              <span className="text-faint">
                on {entry.subject_type}
                {entry.subject_id !== null && ` #${entry.subject_id}`}
              </span>
              <span className="ml-auto text-faint">{entry.created_at}</span>

              {(entry.before_state || entry.after_state) && (
                <pre className="w-full overflow-x-auto rounded bg-app p-2 text-xs text-faint">
                  {JSON.stringify(entry.before_state)} → {JSON.stringify(entry.after_state)}
                </pre>
              )}
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}
